import React, { Component } from 'react'
import PropTypes from 'prop-types'
import IPropTypes from 'react-immutable-proptypes'
import { FormattedMessage } from 'react-intl'
import { DragSource } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'
import { alpha3ToAlpha2 } from 'i18n-iso-countries'
import * as detectIt from 'detect-it'
import cx from 'classnames'
import _ from 'lodash'
import Tooltip, { POSITION } from 'gnos-tooltip'
import ContextMenu, { Item } from 'gnos-context-menu'
import { doubleClick } from 'gnos-utilities/event'

import config from '@config'
import { canGenerateDistraints } from '@global/helpers'

import { DND_TYPE } from '../../dnd/constants'
import { LOCALE_PREFIX } from './constants'

import { getEntityIconClassNames, getEntityText } from './helpers'

import Minireport from '../Minireport'
import CloseButton from '../CloseButton'

import acl from '../../services/acl'
import hocConnect from './hocConnect'
import hocFavourites from '../../scenes/Gnosus/components/EntityStack/components/StackContent/components/FavouritesContent/hocFavourites'

import './style.scss'

const entitySource = {
    beginDrag: (props) => ({
        entity: props.entity,
    }),
}

function collect(connect, monitor) {
    return {
        connectDragSource: connect.dragSource(),
        connectDragPreview: connect.dragPreview(),
        isDragging: monitor.isDragging(),
    }
}

const idFavoritesSelector = (props) => props.entity && props.entity.get('entId')

@acl
@hocConnect
@doubleClick
@hocFavourites(idFavoritesSelector)
@DragSource(DND_TYPE.ENTITY, entitySource, collect)
export default class Entity extends Component {
    static propTypes = {
        activeEntityStackTab: PropTypes.string.isRequired,
        activeModule: PropTypes.string.isRequired,
        addToFavourites: PropTypes.func.isRequired,
        connectDragPreview: PropTypes.func.isRequired,
        connectDragSource: PropTypes.func.isRequired,
        entity: IPropTypes.map.isRequired,
        hasRights: PropTypes.func.isRequired,
        isContextMenuShow: PropTypes.bool.isRequired,
        isDoubleClick: PropTypes.func.isRequired,
        isDragging: PropTypes.bool.isRequired,
        openLinkagesById: PropTypes.func.isRequired,
        openReportById: PropTypes.func.isRequired,
        removeFromFavourites: PropTypes.func.isRequired,
        renderWraps: PropTypes.bool,
        shouldAddToFavourites: PropTypes.bool.isRequired,
        setupDistraintsById: PropTypes.func.isRequired,
    }

    static defaultProps = {
        renderWraps: true,
    }

    componentDidMount() {
        const { connectDragPreview } = this.props

        connectDragPreview(getEmptyImage())
    }

    onEntityClick = (_e) => {
        const { isDoubleClick, openReportById, entity, activeModule } = this.props

        if (isDoubleClick(_e)) {
            switch (activeModule) {
                case config.modules.report.key: {
                    openReportById(_.toString(entity.get('entId')))
                    break
                }
                default: {
                    openReportById(_.toString(entity.get('entId')))
                }
            }
        }
    }

    onContextMenuAddLinkagesClick = () => {
        const { openLinkagesById, entity } = this.props

        openLinkagesById(entity.get('entId'))
    }

    onContextMenuAddReportClick = () => {
        const { openReportById, entity } = this.props

        openReportById(_.toString(entity.get('entId')))
    }

    onAddToFavourites = () => {
        const { entity, addToFavourites } = this.props

        addToFavourites(entity.get('entId'))
    }

    onRemoveFromFavourites = () => {
        const { entity, removeFromFavourites } = this.props

        removeFromFavourites(entity.get('entId'))
    }

    onContextMenuHandleDistraintsClick = () => {
        const { entity, setupDistraintsById } = this.props

        setupDistraintsById(_.toString(entity.get('entId')))
    }

    renderTooltip = (content) => {
        const { entity } = this.props

        const entityId = entity.get('entId')
        const minireport = <Minireport id={entityId} />

        return (
            <Tooltip
                content={minireport}
                id={`tooltip_minireport_${entityId}`}
                position={POSITION.RIGHT}
                width={'320px'}
                showDelay={1000}
                automaticAlignChange
                doubleWrap
            >
                <div className={'wrap-entity-tooltip'}>{content}</div>
            </Tooltip>
        )
    }

    renderContextMenu = (content) => {
        const { activeEntityStackTab, entity, hasRights, shouldAddToFavourites } = this.props

        const items = []

        if (
            activeEntityStackTab === config.components.entityStack.history.key ||
            activeEntityStackTab === config.components.entityStack.search.key
        ) {
            const contextItem = shouldAddToFavourites ? (
                <Item
                    key={'favorite'}
                    type={'favorite'}
                    text={<FormattedMessage id={`${LOCALE_PREFIX}.contextMenu.addToFavorite`} />}
                    onItemClick={this.onAddToFavourites}
                />
            ) : (
                <Item
                    key={'delete'}
                    type={'delete'}
                    text={<FormattedMessage id={`${LOCALE_PREFIX}.contextMenu.delete`} />}
                    onItemClick={this.onRemoveFromFavourites}
                />
            )

            items.push(contextItem)
        }

        const deleteItem =
            activeEntityStackTab === config.components.entityStack.favourites.key ? (
                <Item
                    key={'delete'}
                    type={'delete'}
                    text={<FormattedMessage id={`${LOCALE_PREFIX}.contextMenu.delete`} />}
                    onItemClick={this.onRemoveFromFavourites}
                />
            ) : null

        const reportItem = hasRights(config.modules.report.key) && (
            <Item
                key={'report'}
                type={'report'}
                text={
                    <span>
                        <FormattedMessage id={`${LOCALE_PREFIX}.contextMenu.generateNewReport`} />
                    </span>
                }
                onItemClick={this.onContextMenuAddReportClick}
            />
        )

        const linkagesItem = hasRights(config.modules.linkages.key) && (
            <Item
                key={'linkages'}
                type={'linkages'}
                text={<FormattedMessage id={`${LOCALE_PREFIX}.contextMenu.generateLinkages`} />}
                onItemClick={this.onContextMenuAddLinkagesClick}
            />
        )

        const distraintsItem = hasRights(config.modules.distraints.key) &&
            canGenerateDistraints(alpha3ToAlpha2(entity.get('country', null))) && (
                <Item
                    key={'distraints'}
                    type={'distraints'}
                    text={<FormattedMessage id={`${LOCALE_PREFIX}.contextMenu.generateDistraints`} />}
                    onItemClick={this.onContextMenuHandleDistraintsClick}
                />
            )

        items.push(deleteItem, reportItem, linkagesItem, distraintsItem)

        return (
            <ContextMenu id={`contextMenu_${entity.get('entId')}`} content={items}>
                <div className={'wrap-entity-context-menu'}>{content}</div>
            </ContextMenu>
        )
    }

    render() {
        const { entity, renderWraps, connectDragSource, activeEntityStackTab, isDragging, isContextMenuShow } =
            this.props

        const entityText = getEntityText(entity)
        // TODO country iso should be from backend
        const countryIso = _.toLower(alpha3ToAlpha2(entity.get('country', '')))

        let content = (
            <div className="entity-wrap" onClick={this.onEntityClick}>
                <div className={cx('icon', getEntityIconClassNames(entity))}>
                    {countryIso !== 'cz' && <div className={cx('flag-icon', `flag-icon-${countryIso}`)} />}
                </div>
                <div className="texts">
                    <div className="name">{entity.get('name')}</div>
                    <div className="text">{entityText}</div>
                    <div className="address">{entity.get('address')}</div>
                </div>
                {activeEntityStackTab === 'favourites' && (
                    <div className="close-area">
                        <CloseButton onClick={this.onRemoveFromFavourites} pad={2} size={12} />
                    </div>
                )}
            </div>
        )

        // wrap content to DnD Source component
        if (detectIt.deviceType !== 'touchOnly') content = connectDragSource(content)

        // wrap content to ContextMenu component
        if (renderWraps) content = this.renderContextMenu(content)

        // wrap content to Tooltip with Minireport component
        if (renderWraps && config.components.minireport.enable && !isContextMenuShow && !isDragging)
            content = this.renderTooltip(content)

        return content
    }
}
